// SessionContext — the signed-in user and the views they may access.
//
// Entra authenticates the person; the global allowlist (resolved at callback
// time, ADR-0004) decides which views their session carries. Both the auth
// gate at `/` and the sidebar consume `grantedFrontends`, so a user can never
// switch into a view they have no rights to.

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    grantedFrontends as policyGrantedFrontends,
    type ActingForBook,
    type FrontendKey,
} from '@septima/contract';
import { SESSION_QUERY_KEY, useSessionQuery, type SessionState } from '@/lib/auth';
import { setScenarioClockOffsetMs } from '@/lib/clock';

export interface SessionUser {
    /**
     * The signed-in `users.id`. The canonical identity for the write paths
     * that stamp a staff owner onto a row (e.g. the IM an onboarding enquiry
     * is assigned to) — a display name is not an identity.
     */
    userId: string;
    name: string | null;
    email: string | null;
    /** Raw persona list from the session cookie — includes 'admin' if granted. */
    personas: string[];
    /**
     * The currently-active persona (ADR-0004). One persona governs every
     * read; the sidebar's persona switcher updates it via
     * `/api/auth/persona` which audits the switch. Null on a session
     * minted before this field shipped — the server will fall back to its
     * preference order for read enforcement.
     */
    activePersona: string | null;
    /**
     * The IM books this user may act on, in switcher order. An
     * assistant assigned to several IMs gets one sidebar button for each, in
     * place of the single "Desk" button. Empty for a user who works their own
     * book.
     */
    actingFor: ActingForBook[];
    /**
     * The IM book the Desk shell reads now. The server resolves it against the
     * assignments, so it always names a book the switcher offers. Null for a
     * user who acts for nobody.
     */
    activeActingForUserId: string | null;
}

interface SessionContextType {
    /** The signed-in user, or null while hydrating / when unauthenticated. */
    user: SessionUser | null;
    /** The view keys the signed-in user is granted (allowlist-filtered). */
    grantedFrontends: FrontendKey[];
    /** True while the initial session fetch is in flight. */
    isHydrating: boolean;
    /** The server booted in scenario mode — gates the scenario chrome. */
    scenarioMode: boolean;
    /** Force-refresh the session — used after a persona switch. */
    refresh: () => Promise<void>;
    /**
     * Optimistically set the active persona locally (PRD #156). The shell
     * calls this on a persona switch so the visible state updates
     * immediately, while the server-side switch + audit write reconcile in
     * the background via `refresh`. Recovery paths use it to revert.
     */
    setActivePersona: (persona: string) => void;
    /**
     * Optimistically set the active IM book locally, as
     * {@link SessionContextType.setActivePersona} does for the persona. The
     * sidebar calls it on a book switch so the highlighted button moves at
     * once, while the server-side switch + audit write reconcile through
     * `refresh`.
     */
    setActingForUserId: (userId: string) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    // One shared query backs every session consumer (this provider, the `/`
    // auth gate, the login screen). React Query dedupes them to a single
    // request and a single cache entry. `fetchSession` never rejects, so
    // `data` is the source of truth and `isPending` is the hydration window.
    const { data, isPending } = useSessionQuery();

    // Force a re-fetch — used after a persona switch.
    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }, [queryClient]);

    // Optimistically reflect a persona switch in the shared cache so the UI
    // updates immediately; the server-side switch + audit reconcile via the
    // subsequent `refresh`. Recovery paths reuse this to revert.
    const setActivePersona = useCallback(
        (persona: string) => {
            queryClient.setQueryData<SessionState>(SESSION_QUERY_KEY, (prev) =>
                prev?.user ? { ...prev, user: { ...prev.user, activePersona: persona } } : prev,
            );
        },
        [queryClient],
    );

    // Reflect a book switch in the shared cache at once; the server-side
    // switch + audit reconcile through the subsequent `refresh`.
    const setActingForUserId = useCallback(
        (userId: string) => {
            queryClient.setQueryData<SessionState>(SESSION_QUERY_KEY, (prev) =>
                prev?.user
                    ? { ...prev, user: { ...prev.user, activeActingForUserId: userId } }
                    : prev,
            );
        },
        [queryClient],
    );

    // Mirror the scenario clock offset into the render clock (scenario mode
    // only; 0 elsewhere). Done during render — synchronous, so the very next
    // clockNow() read after a session refetch already reflects an advance.
    setScenarioClockOffsetMs(data?.scenarioClockOffsetMs ?? 0);

    const value = useMemo<SessionContextType>(() => {
        const sessionUser = data?.user ?? null;
        const user: SessionUser | null = sessionUser
            ? {
                  userId: sessionUser.userId,
                  name: sessionUser.name,
                  email: sessionUser.email,
                  personas: sessionUser.personas ?? [],
                  activePersona: sessionUser.activePersona ?? null,
                  actingFor: sessionUser.actingFor ?? [],
                  activeActingForUserId: sessionUser.activeActingForUserId ?? null,
              }
            : null;
        // Resolve the granted frontends through the shared persona policy
        // (ADR-0004): `im-assistant` maps to Desk (no lockout) and `admin`
        // contributes no shell. Default to NONE — never all four: this feeds
        // the sidebar persona switcher and the client route-grant, so an empty
        // / not-yet-loaded persona list must fail closed (grant nothing), not
        // open (offer every shell). The shell renders a Loading state while
        // `isHydrating`, so this default is never user-visible on a cold load.
        const granted = sessionUser?.personas;
        const grantedFrontends: FrontendKey[] =
            granted && granted.length > 0 ? policyGrantedFrontends(granted) : [];
        return {
            user,
            grantedFrontends,
            isHydrating: isPending,
            scenarioMode: data?.scenarioMode ?? false,
            refresh,
            setActivePersona,
            setActingForUserId,
        };
    }, [data, isPending, refresh, setActivePersona, setActingForUserId]);

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
    const context = useContext(SessionContext);
    if (context === undefined) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
}

/**
 * The id of the signed-in user, or null. It does not need a SessionProvider,
 * so a kit component (the DataTable) can key a view preference by the user
 * and still render in a test or on a page outside the shell.
 */
export function useSignedInUserId(): string | null {
    return useContext(SessionContext)?.user?.userId ?? null;
}
